import {
  computed,
  toValue,
  type MaybeRefOrGetter,
} from 'vue'

import type {
  Attachment,
  FineUploaderUpload,
} from '../../models/current-student-advanced-standing-form/api'
import {
  activeAttachmentCount,
  activeNewUploadCount,
  activeSavedAttachmentCount,
  canUploadAttachment,
  showFineUploader,
  uploadItemLimit,
  visibleNewUploads,
} from '../../models/current-student-advanced-standing-form/upload'

type UseUploadAttachmentStateOptions = {
  savedAttachments: MaybeRefOrGetter<Attachment[]>
  deletedAttachmentIds: MaybeRefOrGetter<number[]>
  newAttachments: MaybeRefOrGetter<FineUploaderUpload[]>
  maxAttachments: MaybeRefOrGetter<number>
}

export function useUploadAttachmentState({
  savedAttachments,
  deletedAttachmentIds,
  newAttachments,
  maxAttachments,
}: UseUploadAttachmentStateOptions) {
  const activeSavedAttachmentCountValue = computed(() => {
    return activeSavedAttachmentCount(toValue(savedAttachments), toValue(deletedAttachmentIds))
  })

  const uploadItemLimitValue = computed(() => {
    return uploadItemLimit(activeSavedAttachmentCountValue.value, toValue(maxAttachments))
  })

  const activeNewUploadCountValue = computed(() => {
    return activeNewUploadCount(toValue(newAttachments))
  })

  const activeAttachmentCountValue = computed(() => {
    return activeAttachmentCount(activeSavedAttachmentCountValue.value, activeNewUploadCountValue.value)
  })

  const canUploadAttachmentValue = computed(() => {
    return canUploadAttachment(activeAttachmentCountValue.value, toValue(maxAttachments))
  })

  const visibleNewAttachments = computed(() => {
    return visibleNewUploads(toValue(newAttachments))
  })

  const showFineUploaderValue = computed(() => {
    return showFineUploader(canUploadAttachmentValue.value, visibleNewAttachments.value.length)
  })

  return {
    activeSavedAttachmentCount: activeSavedAttachmentCountValue,
    uploadItemLimit: uploadItemLimitValue,
    activeNewUploadCount: activeNewUploadCountValue,
    activeAttachmentCount: activeAttachmentCountValue,
    canUploadAttachment: canUploadAttachmentValue,
    visibleNewAttachments,
    showFineUploader: showFineUploaderValue,
  }
}
